// personnelActivityHistory.js - Activity History modal for Personnel Metrics.
// Receives the selected personnel object as a prop.
// Rendered inside the shared Modal component.
//
// ── Data flow ─────────────────────────────────────────────────────────────────
//
//   Modal passes { person } prop — shape comes from fetchPersonnelList():
//     { name, objId, employeeNumber, department }
//     → usePersonnelHistory(person.employeeNumber)  fetches live SQL rows
//       → sortedData fed into the sortable table
//
// ── Status derivation ─────────────────────────────────────────────────────────
//
//   RTLS_PersonnelMetricsActivityHist has no status column.  Status is derived
//   client-side from the row's endTime string:
//
//     endTime === "12:00 AM"  →  "In Progress"   (sentinel used by the ETL)
//     endTime is null         →  "Pending"
//     anything else           →  "Completed"

import React from "react";
import useTableSort from "../../../hooks/useTableSort";
import SortableTableHeader from "../../modalContent/SortableTableHeader";
import usePersonnelHistory from "../../../hooks/usePersonnelHistory";

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Derive a display status from the raw endTime string returned by the backend.
 *
 * The ETL writes "12:00 AM" as the EndTime sentinel for jobs that are still
 * running.  A null EndTime means the job has been assigned but not yet started.
 *
 * @param {string | null} endTime
 * @returns {"In Progress" | "Pending" | "Completed"}
 */
function deriveStatus(endTime) {
  if (endTime === null || endTime === undefined) return "Pending";
  if (endTime.trim() === "12:00 AM") return "In Progress";
  return "Completed";
}

/**
 * Parse a "hh:mm AM/PM" string into minutes-since-midnight.
 * Returns null on anything that doesn't match (missing/garbled startTime).
 *
 * @param {string | null} timeStr
 * @returns {number | null}
 */
function parseTimeToMinutes(timeStr) {
  if (!timeStr) return null;
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(timeStr.trim());
  if (!match) return null;

  const [, hh, mm, ampm] = match;
  let hours = parseInt(hh, 10) % 12;
  if (ampm.toUpperCase() === "PM") hours += 12;

  return hours * 60 + parseInt(mm, 10);
}

/**
 * Build a single sortable ISO timestamp from the row's eventDate (calendar
 * day) and startTime (raw "hh:mm AM/PM" string). This is what the "Date &
 * Time" column actually sorts on — sorting startTime alone as a string is
 * unreliable ("01:00 PM" < "09:00 AM" alphabetically), so we fold both
 * fields into one real Date.
 *
 * Date components are read in UTC to match how EventDate is stored/returned
 * by the backend, so the calendar day shown never shifts due to the
 * browser's local timezone.
 *
 * @param {string | null} eventDate - ISO-8601 string
 * @param {string | null} startTime - "hh:mm AM/PM"
 * @returns {string | null} ISO-8601 timestamp, or null if eventDate is unusable
 */
function buildSortableDateTime(eventDate, startTime) {
  if (!eventDate) return null;

  const parsed = new Date(eventDate);
  if (isNaN(parsed.getTime())) return null;

  const base = new Date(
    Date.UTC(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth(),
      parsed.getUTCDate(),
      0,
      0,
      0,
    ),
  );

  const minutes = parseTimeToMinutes(startTime);
  if (minutes != null) {
    base.setUTCMinutes(base.getUTCMinutes() + minutes);
  }

  return base.toISOString();
}

/**
 * Format eventDate as a short display label, e.g. "Jun 22".
 * Reads date components in UTC for the same reason as buildSortableDateTime.
 *
 * @param {string | null} eventDate
 * @returns {string}
 */
function formatDisplayDate(eventDate) {
  if (!eventDate) return "—";
  const d = new Date(eventDate);
  if (isNaN(d.getTime())) return "—";

  return d.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

const JobStatusBadge = ({ status }) => {
  const cls = {
    Completed: "pm-job-badge--completed",
    "In Progress": "pm-job-badge--inprogress",
    Pending: "pm-job-badge--pending",
  };
  return <span className={`pm-job-badge ${cls[status] || ""}`}>{status}</span>;
};

// ── Component ─────────────────────────────────────────────────────────────────

const PersonnelActivityHistory = ({ person }) => {
  // ── Fetch live activity history for this employee ──────────────────────────
  const { activityHistory, loading, error } = usePersonnelHistory(
    person?.employeeNumber,
  );

  // ── Derive status + sortable date/time on each row before handing to the
  //    sort hook ──────────────────────────────────────────────────────────────
  //
  // useTableSort receives a stable array reference when activityHistory is
  // empty (EMPTY_HISTORY constant in the hook), so this map only runs when
  // real rows are present.
  const rowsWithStatus = activityHistory.map((row) => ({
    ...row,
    status: deriveStatus(row.endTime),
    jobDateTime: buildSortableDateTime(row.eventDate, row.startTime),
  }));

  // ── Table sorting ──────────────────────────────────────────────────────────
  //
  // Default sort is chronological (oldest first), matching the backend's own
  // ORDER BY StartTime ASC — makes sense as the default view now that a
  // single employee can have rows spanning several days in the current week.
  const { sortedData, requestSort, getSortClass } = useTableSort(
    rowsWithStatus,
    {
      initialKey: "jobDateTime",
      initialDirection: "asc",
    },
  );

  // ── Render: loading ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <p className="modal-description">
          This week's job history for {person.name} &mdash;{" "}
          {person.employeeNumber} &bull; {person.department}
        </p>
        <div className="modal-section">
          <p className="modal-loading">Loading activity history&hellip;</p>
        </div>
      </>
    );
  }

  // ── Render: error ──────────────────────────────────────────────────────────
  if (error) {
    return (
      <>
        <p className="modal-description">
          This week's job history for {person.name} &mdash;{" "}
          {person.employeeNumber} &bull; {person.department}
        </p>
        <div className="modal-section">
          <p className="modal-error">
            Unable to load activity history. Please try again.
          </p>
        </div>
      </>
    );
  }

  // ── Render: table ──────────────────────────────────────────────────────────
  return (
    <>
      <p className="modal-description">
        This week's job history for {person.name} &mdash;{" "}
        {person.employeeNumber} &bull; {person.department}
      </p>
      <div className="modal-section modal-section--scroll">
        <h3>Job Activity</h3>
        <div className="modal-table-scroll">
          <table className="modal-table">
            <thead>
              <tr>
                <SortableTableHeader
                  columnKey="jobName"
                  onSort={requestSort}
                  sortClass={getSortClass("jobName")}
                >
                  Job Name
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="jobId"
                  onSort={requestSort}
                  sortClass={getSortClass("jobId")}
                >
                  Job ID
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="status"
                  onSort={requestSort}
                  sortClass={getSortClass("status")}
                >
                  Status
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="duration"
                  onSort={requestSort}
                  sortClass={getSortClass("duration")}
                >
                  Duration
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="jobDateTime"
                  onSort={requestSort}
                  sortClass={getSortClass("jobDateTime")}
                >
                  Date
                </SortableTableHeader>
              </tr>
            </thead>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="modal-table__empty">
                    No activity recorded this week.
                  </td>
                </tr>
              ) : (
                sortedData.map((row) => (
                  <tr key={`${row.jobId}-${row.eventDate}-${row.startTime}`}>
                    <td>{row.jobName}</td>
                    <td>{row.jobId}</td>
                    <td>
                      <JobStatusBadge status={row.status} />
                    </td>
                    <td>{row.duration}</td>
                    <td>
                      <div className="pm-job-datetime">
                        <span className="pm-job-date">
                          {formatDisplayDate(row.eventDate)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default PersonnelActivityHistory;
